import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthLayoutComponent } from '../layout/auth-layout.component';
import { ToastService } from '@victory-lib-ui/shared/utils';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LoggerService } from '@victory-lib-ui/shared/utils';
import { ENVIRONMENT_CONFIG } from '@victory-lib-ui/shared/data-access';
import { resolveAuthFailureMessageKey } from '../../utils/auth-feedback.utils';

@Component({
  selector: 'lib-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    AuthLayoutComponent,
    TranslocoModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['../../styles/auth-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(TranslocoService);
  private readonly environment = inject(ENVIRONMENT_CONFIG);

  showPassword = signal(false);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  loginForm: FormGroup;

  constructor() {
    const demoLoginConfig = this.environment.demoConfig?.login;
    const demoEmail =
      demoLoginConfig?.email ??
      this.environment.demoConfig?.demoUser?.email ??
      '';
    const demoPassword = demoLoginConfig?.password ?? '';

    this.loginForm = this.fb.group({
      email: [demoEmail, [Validators.required, Validators.email]],
      password: [demoPassword, [Validators.required]],
      rememberMe: [demoLoginConfig?.rememberMe ?? true],
    });

    if (this.environment.demoConfig?.autoLogin && demoLoginConfig?.autoSubmit) {
      queueMicrotask(() => this.onSubmit());
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword.update((show) => !show);
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      const key = 'login.error_fill_required';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const credentials = this.loginForm.value;

    this.authService.login(credentials).subscribe({
      next: (response) => {
        if (response.success) {
          this.logger.info('Login successful', {
            userId: response.data?.user?.id,
          });
          this.router.navigate(['/dashboard']);
        } else {
          const key = 'login.error_unexpected';
          this.errorMessage.set(this.i18n.translate(key));
          this.toast.show(key, 'error');
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        this.logger.error('Login error', error);
        const key = resolveAuthFailureMessageKey('login', error);
        this.errorMessage.set(this.i18n.translate(key));
        this.toast.show(key, 'error');
        this.isLoading.set(false);
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onSocialLogin(provider: string): void {
    this.toast.show(
      this.i18n.translate('login.social_coming_soon', { provider }),
      'info'
    );
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }
}
